import React from "react";
import {
  Grid,
  Button,
  Stack,
  Typography,
  Box,
  CircularProgress,
  Chip,
} from "@mui/material";
import { NavigateNext } from "@mui/icons-material";
import { useFormik } from "formik";
import * as Yup from "yup";
import {
  CustomFormLabel,
  CustomTextField,
} from "../../../../components/formsComponents";
import { MuiNumberField } from "../../../../components/formsComponents/MuiNumberField";
import { usePaymentReminderConfig } from "../context/PaymentReminderConfig.context";
import { useBillingCompanyContext } from "../../../../providers";
import { fetchCampaignRuleTemplates } from "../services/paymentReminderConfig.service";
import { ICampaignRuleTemplate } from "../../../../redux/marketCampaign/marketCampaign.types";

/**
 * Check if a specific variable is filled.
 */
const isVariableFilled = (
  type: string,
  value: any,
): boolean => {
  switch (type) {
    case "multiselect_dropdown":
      return Array.isArray(value) && value.length > 0;
    case "dropdown":
      return typeof value === "string" && value.trim() !== "";
    case "number":
      return value !== null && value !== undefined && value !== "" && value !== 0;
    default:
      return value !== null && value !== undefined && value !== "";
  }
};

/**
 * Check if all template variables are filled.
 */
const areAllVariablesFilled = (
  variablesDetails: ICampaignRuleTemplate["variables_details"],
  campaignRule: Record<string, any> | null,
): boolean => {
  if (!variablesDetails?.length) return true;
  if (!campaignRule) return false;

  return variablesDetails.every(({ variable, type }) => {
    const value = campaignRule[variable];
    switch (type) {
      case "multiselect_dropdown":
        return Array.isArray(value) && value.length > 0;
      case "dropdown":
        return typeof value === "string" && value.trim() !== "";
      case "number":
        return value !== null && value !== undefined && value !== "" && value !== 0;
      default:
        return value !== null && value !== undefined && value !== "";
    }
  });
};

const validationSchema = Yup.object({
  configuration_name: Yup.string().required("Configuration name is required"),
});

export const Step1DetailsAndRule: React.FC = () => {
  const { formState, updateFormState, goNext } = usePaymentReminderConfig();
  const { comapny } = useBillingCompanyContext();

  const [ruleTemplate, setRuleTemplate] =
    React.useState<ICampaignRuleTemplate | null>(null);
  const [loadingTemplates, setLoadingTemplates] = React.useState(true);
  const [campaignRule, setCampaignRule] = React.useState<Record<string, any>>(
    (formState.campaign_rule as Record<string, any>) || {},
  );
  const [showRuleErrors, setShowRuleErrors] = React.useState(false);

  // Fetch rule templates on mount — use the first (only) one
  React.useEffect(() => {
    const loadTemplates = async () => {
      setLoadingTemplates(true);
      const templates = await fetchCampaignRuleTemplates(
        comapny.company_outlet_uuid,
      );
      // This flow always has one template — use the first enabled one
      const template =
        templates.find((t) => t.is_enable) || templates[0] || null;
      setRuleTemplate(template);

      // Initialize campaign_rule if not already set from formState
      if (template && Object.keys(campaignRule).length === 0) {
        const rule: Record<string, any> = {};
        template.variables_details?.forEach((v) => {
          switch (v.type) {
            case "multiselect_dropdown":
              rule[v.variable] = [];
              break;
            case "number":
              rule[v.variable] = "";
              break;
            default:
              rule[v.variable] = "";
          }
        });
        setCampaignRule(rule);
      }

      setLoadingTemplates(false);
    };
    if (comapny.company_outlet_uuid) {
      loadTemplates();
    }
  }, [comapny.company_outlet_uuid]);

  const formik = useFormik({
    initialValues: {
      configuration_name: formState.configuration_name,
      configuration_description: formState.configuration_description,
    },
    validationSchema,
    enableReinitialize: true,
    onSubmit: (values) => {
      // Check if rule template variables are filled
      const variablesFilled = areAllVariablesFilled(
        ruleTemplate?.variables_details || [],
        campaignRule,
      );
      if (!variablesFilled) {
        setShowRuleErrors(true);
        return;
      }

      updateFormState({
        configuration_name: values.configuration_name,
        configuration_description: values.configuration_description,
        market_campaign_rule_template_uuid:
          ruleTemplate?.market_campaign_rule_template_uuid || "",
        market_campaign_rule_template_name:
          ruleTemplate?.template_name || "",
        campaign_rule: campaignRule as any,
        mapping_keys: (ruleTemplate as any)?.mapping_keys || {},
      });
      goNext();
    },
  });

  // Handle variable change
  const handleVariableChange = (variableName: string, newValue: any) => {
    setCampaignRule((prev) => ({
      ...prev,
      [variableName]: newValue,
    }));
  };

  // Handle chip toggle for multiselect
  const handleChipToggle = (variableName: string, option: string) => {
    setCampaignRule((prev) => {
      const current = prev[variableName] || [];
      const updated = current.includes(option)
        ? current.filter((v: string) => v !== option)
        : [...current, option];
      return { ...prev, [variableName]: updated };
    });
  };

  return (
    <form onSubmit={formik.handleSubmit}>
      <Grid container spacing={2}>
        {/* Left: Basic Details | Right: Event Rule Configuration */}
        <Grid item xs={12} md={5}>
          <Typography variant="h5" fontWeight={600} mb={2}>
            Basic Details
          </Typography>
          <CustomTextField
            name="configuration_name"
            label="Configuration Name *"
            placeholder="Enter configuration name"
            fullWidth
            value={formik.values.configuration_name}
            onChange={formik.handleChange}
            onBlur={formik.handleBlur}
            error={
              formik.touched.configuration_name &&
              Boolean(formik.errors.configuration_name)
            }
            helperText={
              formik.touched.configuration_name &&
              formik.errors.configuration_name
            }
          />
          <Box mt={2}>
            <CustomTextField
              name="configuration_description"
              label="Description"
              placeholder="Enter a brief description"
              fullWidth
              multiline
              rows={3}
              value={formik.values.configuration_description}
              onChange={formik.handleChange}
              onBlur={formik.handleBlur}
            />
          </Box>
        </Grid>

        <Grid item xs={12} md={7}>
          <Typography variant="h5" fontWeight={600} mb={1}>
            Event Rule Configuration
          </Typography>

          {loadingTemplates ? (
            <Box sx={{ display: "flex", alignItems: "center", gap: 1, py: 2 }}>
              <CircularProgress size={20} />
              <Typography variant="body2" color="text.secondary">
                Loading rule configuration...
              </Typography>
            </Box>
          ) : !ruleTemplate ? (
            <Typography variant="body2" color="text.secondary" py={2}>
              No rule template available for this outlet.
            </Typography>
          ) : (
            <>
              <Grid container spacing={1}>
                {(ruleTemplate.variables_details || []).map((varDetail) => {
                  switch (varDetail.type) {
                    case "multiselect_dropdown":
                      return (
                        <Grid item xs={12} key={varDetail.variable}>
                          <CustomFormLabel>
                            {varDetail.variable
                              .replace(/_/g, " ")
                              .replace(/\b\w/g, (c) => c.toUpperCase())}{" "}
                            *
                          </CustomFormLabel>
                          <Box
                            sx={{ display: "flex", flexWrap: "wrap", gap: 1 }}
                          >
                            {(varDetail.options || []).map((option) => (
                              <Chip
                                key={option}
                                label={option}
                                clickable
                                color={
                                  (campaignRule[varDetail.variable] || []).includes(
                                    option,
                                  )
                                    ? "primary"
                                    : "default"
                                }
                                variant={
                                  (campaignRule[varDetail.variable] || []).includes(
                                    option,
                                  )
                                    ? "filled"
                                    : "outlined"
                                }
                                onClick={() =>
                                  handleChipToggle(varDetail.variable, option)
                                }
                              />
                            ))}
                          </Box>
                          {showRuleErrors &&
                            !isVariableFilled(
                              varDetail.type,
                              campaignRule[varDetail.variable],
                            ) && (
                              <Typography variant="caption" color="error" mt={0.5}>
                                Please select at least one option
                              </Typography>
                            )}
                        </Grid>
                      );

                    case "number":
                      return (
                        <Grid item xs={12} md={6} key={varDetail.variable}>
                          <CustomFormLabel>
                            {(varDetail as any).label ||
                              varDetail.variable
                                .replace(/_/g, " ")
                                .replace(/\b\w/g, (c) => c.toUpperCase())}{" "}
                            *
                          </CustomFormLabel>
                          <MuiNumberField
                            name={varDetail.variable}
                            label=""
                            value={campaignRule[varDetail.variable] || ""}
                            onChange={(e) =>
                              handleVariableChange(
                                varDetail.variable,
                                e.target.value,
                              )
                            }
                            placeholder="Enter value"
                            sx={{ width: "100%" }}
                            error={
                              showRuleErrors &&
                              !isVariableFilled(
                                varDetail.type,
                                campaignRule[varDetail.variable],
                              )
                                ? "This field is required"
                                : undefined
                            }
                          />
                        </Grid>
                      );

                    case "dropdown":
                      return (
                        <Grid item xs={12} md={6} key={varDetail.variable}>
                          <CustomFormLabel>
                            {varDetail.variable
                              .replace(/_/g, " ")
                              .replace(/\b\w/g, (c) => c.toUpperCase())}{" "}
                            *
                          </CustomFormLabel>
                          <Box
                            sx={{ display: "flex", flexWrap: "wrap", gap: 1 }}
                          >
                            {(varDetail.options || []).map((option) => (
                              <Chip
                                key={option}
                                label={option}
                                clickable
                                color={
                                  campaignRule[varDetail.variable] === option
                                    ? "primary"
                                    : "default"
                                }
                                variant={
                                  campaignRule[varDetail.variable] === option
                                    ? "filled"
                                    : "outlined"
                                }
                                onClick={() =>
                                  handleVariableChange(varDetail.variable, option)
                                }
                              />
                            ))}
                          </Box>
                          {showRuleErrors &&
                            !isVariableFilled(
                              varDetail.type,
                              campaignRule[varDetail.variable],
                            ) && (
                              <Typography variant="caption" color="error" mt={0.5}>
                                Please select an option
                              </Typography>
                            )}
                        </Grid>
                      );

                    default:
                      return (
                        <Grid item xs={12} md={6} key={varDetail.variable}>
                          <CustomFormLabel>
                            {varDetail.variable
                              .replace(/_/g, " ")
                              .replace(/\b\w/g, (c) => c.toUpperCase())}{" "}
                            *
                          </CustomFormLabel>
                          <CustomTextField
                            name={varDetail.variable}
                            fullWidth
                            value={campaignRule[varDetail.variable] || ""}
                            onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                              handleVariableChange(
                                varDetail.variable,
                                e.target.value,
                              )
                            }
                            placeholder="Enter value"
                          />
                        </Grid>
                      );
                  }
                })}
              </Grid>

              {/* Rule description preview */}
              <Box
                sx={{
                  p: 1.5,
                  bgcolor: "action.hover",
                  borderRadius: 1,
                  mt: 1,
                }}
              >
                <Typography variant="body2" color="text.secondary">
                  <strong>Rule:</strong>{" "}
                  {ruleTemplate.template
                    ?.split(/(@@\w+)/g)
                    .map((part, idx) => {
                      if (part.startsWith("@@")) {
                        const varName = part.replace("@@", "");
                        const value = campaignRule[varName];
                        const display = Array.isArray(value)
                          ? value.join(", ") || "..."
                          : value || "...";
                        return (
                          <strong key={idx}>{display}</strong>
                        );
                      }
                      return <span key={idx}>{part}</span>;
                    })}
                </Typography>
              </Box>
            </>
          )}
        </Grid>

        {/* Actions - full width */}
        <Grid item xs={12}>
          <Stack direction="row" justifyContent="flex-end" mt={1}>
            <Button
              variant="contained"
              endIcon={<NavigateNext />}
              type="submit"
              disabled={loadingTemplates}
            >
              Next: Template Selection
            </Button>
          </Stack>
        </Grid>
      </Grid>
    </form>
  );
};
